import { HandlerEvent } from 'post-robot';
import { error, ValidationError } from 'common/errors';
import {
  pushErrors,
  pushCompleteIdentityValidationRequest,
  pushCompleteIdentityValidationResponse
} from 'common/gamp';
import { saveUser, UserProfile } from 'communicator/store';
import { getAccessToken, getIsVisaSRCi } from 'communicator/store/selectors';
import { fetchProbe } from 'communicator/utils/api/probe';
import { getUsernameFromRememberMe } from 'communicator/utils/cookies';
import { getHeader } from 'communicator/utils/fetch';
import { getIdToken } from 'communicator/utils/id-token';
import { markStart, markEnd } from 'communicator/utils/instrumentation';
import unbind from 'communicator/utils/unbind';
import {
  CompleteIdentityValidationData,
  CompleteIdentityValidationPayload
} from 'types/visa-sdk/complete-identity-validation';

export default async function completeIdentityValidationLegacy(
  event: HandlerEvent<CompleteIdentityValidationData>
): Promise<CompleteIdentityValidationPayload | ValidationError> {
  try {
    const start = Date.now();
    markStart('COMPLETE_IDENTITY_VALIDATION_LEGACY');
    const accessToken = getAccessToken()?.token ?? '';

    pushCompleteIdentityValidationRequest();

    const probeResponse = await fetchProbe({
      accessToken,
      otpCode: event.data.validationData
    });

    pushCompleteIdentityValidationResponse(probeResponse.ok, Date.now() - start);

    if (!probeResponse.ok) {
      const errorString = probeResponse.errorObject.data?.result?.status || 'UNKNOWN_ERROR';

      if (errorString === 'ACCT_INACCESSIBLE') {
        saveUser({
          accessToken: null,
          isLocked: true
        });
      }

      // TODO: other possible errors from EMVCo spec?
      // - CODE_INVALID (from backend)
      // - CODE_EXPIRED
      // - RETRIES_EXCEEDED (from backend?)
      // - VALIDATION_IN_PROGRESS

      markEnd('COMPLETE_IDENTITY_VALIDATION_LEGACY', {
        doFlush: true,
        failure: true,
        reason: errorString
      });

      // START GENAI@GHCOPILOT
      pushErrors('COMPLETE_IDENTITY_VALIDATION_LEGACY', errorString);
      // END GENAI@GHCOPILOT

      return error(errorString);
    }

    const authStatus = probeResponse.data.auth_status;
    // PARTIAL implies TNC flow
    const isTNC = authStatus === 'PARTIAL';

    const userProfileUpdates: UserProfile = {
      accessToken: {
        expiresIn: probeResponse.data.expires_in,
        isPartial: false,
        token: probeResponse.data.access_token
      },
      isLoggedInOtpUser: true
    };

    if (isTNC) {
      userProfileUpdates.tnc = {
        status: 'not_accepted',
        tncEntryInfo: probeResponse.data.data?.terms?.tncEntryInfo
      };
    }

    const rememberMeUserName = getUsernameFromRememberMe();

    // When isRecognized is true but user does otp login with a different email,
    // revoke recognized tokens and clear erm/ssi cookies
    if (rememberMeUserName) {
      await unbind({
        accessToken: probeResponse.data.access_token,
        ignoreAccessToken: true,
        marks: {
          collection: 'complete_identity_validation_revoke_all_tokens',
          revoke: 'complete_identity_validation_revoke_token_erm'
        }
      });

      userProfileUpdates.isRecognizedUser = false;
    }

    saveUser(userProfileUpdates);

    if (authStatus === 'FULL' || isTNC) {
      // TODO: postmessage session expiry to visa srci?
      const idToken = getIdToken(probeResponse.data.issued_tokens ?? []);

      if (idToken) {
        saveUser({ idToken });
      }

      const payload: CompleteIdentityValidationPayload = {
        idToken
      };

      if (getIsVisaSRCi()) {
        payload['x-via-hint_vch'] = getHeader(probeResponse, 'X-DELIVERY-ADDRESS-TYPE');
      }

      markEnd('COMPLETE_IDENTITY_VALIDATION_LEGACY', { doFlush: true });
      return payload;
    }

    markEnd('COMPLETE_IDENTITY_VALIDATION_LEGACY', {
      doFlush: true,
      failure: true,
      reason: 'UNKNOWN_ERROR'
    });

    // START GENAI@GHCOPILOT
    pushErrors('COMPLETE_IDENTITY_VALIDATION_LEGACY', 'UNKNOWN_ERROR ID_TOKEN returned null');
    // END GENAI@GHCOPILOT

    return {
      idToken: null
    };
  } catch (err: unknown) {
    // START GENAI@GHCOPILOT
    pushErrors('COMPLETE_IDENTITY_VALIDATION_LEGACY', err);
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const sErr = err && JSON.stringify(err) !== undefined ? JSON.stringify(err) : 'Unknown error';

    markEnd('COMPLETE_IDENTITY_VALIDATION_LEGACY', {
      doFlush: true,
      failure: true,
      reason: sErr
    });

    return error('COMPLETE_IDENTITY_VALIDATION_LEGACY_ERROR', sErr);
    // END GENAI@GHCOPILOT
  }
}
